import { z } from 'zod';
import type { GoalPeriod, GoalProgress } from '../calc/savings';
import { SAVINGS_RECURRENCES, type SavingsRecurrence } from '../types';
import {
  centsSchema,
  dayOfMonthSchema,
  isoDateSchema,
  positiveCentsSchema,
  uuidSchema,
} from './common';

/**
 * Un pôle d'épargne. `accountId` dit où dort l'argent, `fundingAccountId` d'où
 * il vient : plusieurs pôles peuvent partager un même livret, c'est la réalité
 * française du Livret A qui sert à tout.
 */
export const savingsGoalCreateSchema = z.object({
  name: z.string().trim().min(1, 'Nom obligatoire').max(60),
  accountId: uuidSchema,
  fundingAccountId: uuidSchema,
  targetAmountCents: positiveCentsSchema.nullable().optional(),
  /** Première échéance, ou échéance unique si la cadence vaut `none`. */
  targetDate: isoDateSchema.nullable().optional(),
  recurrence: z.enum(SAVINGS_RECURRENCES).optional(),
  /** Virement permanent : c'est CE montant que le budget retient. */
  monthlyMinimumCents: centsSchema.min(0).optional(),
  transferDay: dayOfMonthSchema.nullable().optional(),
  startedOn: isoDateSchema.optional(),
  icon: z.string().trim().max(8).optional(),
  color: z.string().trim().max(20).nullable().optional(),
});

export const savingsGoalUpdateSchema = savingsGoalCreateSchema.partial();

export type SavingsGoalCreateInput = z.infer<typeof savingsGoalCreateSchema>;
export type SavingsGoalUpdateInput = z.infer<typeof savingsGoalUpdateSchema>;

/** Virement libre, en plus du permanent. */
export const savingsTransferSchema = z.object({
  amountCents: positiveCentsSchema,
  /**
   * Compte émetteur. Par défaut celui qui alimente habituellement le pôle,
   * mais un versement exceptionnel peut venir d'ailleurs.
   */
  fromAccountId: uuidSchema.optional(),
  occurredOn: isoDateSchema.optional(),
  label: z.string().trim().max(120).optional(),
});

export type SavingsTransferInput = z.infer<typeof savingsTransferSchema>;

/**
 * Retrait : le pôle renfloue un compte, typiquement le courant à sec.
 * `toAccountId` est explicite plutôt que déduit du pôle — le compte à
 * renflouer est celui que l'écran affichait au moment de l'alerte, pas
 * nécessairement celui qui alimente habituellement ce pôle.
 */
export const savingsWithdrawSchema = z.object({
  toAccountId: uuidSchema,
  amountCents: positiveCentsSchema,
  occurredOn: isoDateSchema.optional(),
  label: z.string().trim().max(120).optional(),
});

export type SavingsWithdrawInput = z.infer<typeof savingsWithdrawSchema>;

export interface SavingsGoalResponse {
  id: string;
  name: string;
  accountId: string;
  fundingAccountId: string;
  icon: string;
  color: string | null;
  targetAmountCents: number | null;
  targetDate: string | null;
  recurrence: SavingsRecurrence;
  monthlyMinimumCents: number;
  transferDay: number | null;
  startedOn: string;
  archivedAt: string | null;
  /** Solde total du pôle : virements reçus moins pioches, depuis toujours. */
  savedCents: number;
  /**
   * Ce qui compte pour l'objectif en cours. Identique au solde pour un objectif
   * unique ; limité à la fenêtre courante pour un objectif récurrent.
   */
  periodSavedCents: number;
  /** Fenêtre de l'objectif en cours, nulle si le pôle n'a pas d'échéance. */
  period: GoalPeriod | null;
  progress: GoalProgress;
}

export interface SavingsOverviewResponse {
  goals: SavingsGoalResponse[];
  /**
   * Part des comptes d'épargne qui n'est rattachée à aucun pôle. Un livret qui
   * sert à tout finit toujours par en avoir.
   */
  unassignedCents: number;
}
